/**
 * MovimentacoesPage Component
 * 
 * Página de listagem de movimentações de estoque em formato de cards.
 * Exibe movimentações em cards com informações resumidas.
 * 
 * Funcionalidades:
 * - Barra de pesquisa
 * - Lista em cards (não tabela)
 * - Primeiro card com botão "Nova Movimentação"
 * - Informações: código, descrição, estoque, tipo, status, data/hora
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, ArrowUpDown, CheckCircle, XCircle, Eye, Trash2 } from 'lucide-react'
import { stockMovementService } from '../../../../services/erpApi'
import { productCacheHelpers } from '../../../../utils/productCacheEvents'
import './MovimentacoesPage.css'

function MovimentacoesPage() {
  const navigate = useNavigate()
  const [movimentacoes, setMovimentacoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const loadMovimentacoes = useCallback(async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      }

      const response = await stockMovementService.getAll(params)
      if (response.success) {
        let data = response.data || []
        
        // Filtrar por termo de busca se houver
        if (searchTerm) {
          data = data.filter(m => 
            m.movement_type_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.movement_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            m.observation?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        }
        
        setMovimentacoes(data)
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar movimentações:', error)
    } finally {
      setLoading(false)
    }
  }, [pagination.limit, pagination.page, searchTerm])

  useEffect(() => {
    loadMovimentacoes()
  }, [loadMovimentacoes])

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getMovementTypeLabel = (type) => {
    switch (type) {
      case 'entry':
        return 'Entrada'
      case 'exit':
        return 'Saída'
      case 'transfer':
        return 'Transferência'
      case 'adjustment':
        return 'Ajuste'
      default:
        return type
    }
  }

  const getMovementTypeColor = (type) => {
    switch (type) {
      case 'entry':
        return 'entry'
      case 'exit':
        return 'exit'
      case 'transfer':
        return 'transfer'
      default:
        return ''
    }
  }

  const getStatusLabel = (status) => {
    // Por enquanto, todas as movimentações são consideradas aceitas
    // Futuramente, quando houver campo de status no backend, usar esse campo
    return 'Aceita'
  }

  const getStatusColor = (status) => {
    // Por enquanto, todas são aceitas
    return 'accepted'
  }

  const handleView = (mov) => {
    // Abrir modal ou página de visualização
    console.log('Visualizar movimentação:', mov)
    // TODO: Implementar visualização detalhada
  }

  const handleDelete = async (mov) => {
    if (!window.confirm(`Tem certeza que deseja excluir a movimentação ${mov.movement_type_code || mov.id}?`)) {
      return
    }

    try {
      await stockMovementService.delete(mov.id)
      
      // Notificar movimentação de estoque revertida para o produto afetado
      if (mov.product_id) {
        productCacheHelpers.notifyStockMovement(mov.product_id, {
          quantity: mov.quantity,
          movementType: mov.movement_type_id,
          deleted: true
        });
      }
      
      loadMovimentacoes()
    } catch (error) {
      console.error('Erro ao excluir movimentação:', error)
      alert('Erro ao excluir movimentação. Tente novamente.')
    }
  }

  return (
    <div className="movimentacoes-page">
      <div className="page-header">
        <div>
          <h1>
            <ArrowUpDown size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Movimentações de Estoque
          </h1>
          <p>Gerencie as movimentações de estoque</p>
        </div>
      </div>

      <div className="search-bar">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Pesquisar movimentações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <p>Carregando movimentações...</p>
        </div>
      ) : (
        <div className="movements-list">
          {/* Primeiro card: Nova Movimentação */}
          <div className="movement-card new-movement-card">
            <div className="card-content">
              <div className="card-icon">
                <Plus size={32} />
              </div>
              <div className="card-info">
                <h3>Nova Movimentação</h3>
                <p>Criar uma nova movimentação de estoque</p>
              </div>
              <button
                className="btn-new-movement"
                onClick={() => navigate('/erp/estoque/movimentacoes/nova')}
              >
                Nova Movimentação
              </button>
            </div>
          </div>

          {/* Cards de movimentações */}
          {movimentacoes.length === 0 ? (
            <div className="empty-state">
              <p>Nenhuma movimentação encontrada</p>
            </div>
          ) : (
            movimentacoes.map((mov) => (
              <div key={mov.id} className="movement-card">
                <div className="card-header">
                  <div className="movement-code">
                    <span className="code-label">Código:</span>
                    <span className="code-value">{mov.movement_type_code || `MOV-${mov.id}`}</span>
                  </div>
                  <div className="header-badges">
                    <div className={`type-badge-header ${getMovementTypeColor(mov.movement_type)}`}>
                      {getMovementTypeLabel(mov.movement_type)}
                    </div>
                    <div className={`status-badge ${getStatusColor(mov.status)}`}>
                      {getStatusLabel(mov.status) === 'Aceita' ? (
                        <CheckCircle size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      <span>{getStatusLabel(mov.status)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="movement-description">
                    <strong>Movimentação:</strong>
                    <span>{mov.movement_type_name || '-'}</span>
                  </div>
                  
                  <div className="movement-details">
                    <div className="detail-item">
                      <strong>Produto:</strong>
                      <span>{mov.product_name || '-'}</span>
                    </div>
                    
                    <div className="detail-item">
                      <strong>Quantidade:</strong>
                      <span className={mov.movement_type === 'exit' ? 'quantity-exit' : 'quantity-entry'}>
                        {mov.movement_type === 'exit' ? '-' : '+'}{mov.quantity}
                      </span>
                    </div>
                    
                    {mov.observation && (
                      <div className="detail-item full-width">
                        <strong>Observação:</strong>
                        <span>{mov.observation}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="card-footer">
                  <div className="footer-left">
                    <div className="movement-date">
                      <strong>Data/Hora:</strong>
                      <span>{formatDate(mov.created_at)}</span>
                    </div>
                    {mov.created_by_name && (
                      <div className="movement-user">
                        <strong>Usuário:</strong>
                        <span>{mov.created_by_name}</span>
                      </div>
                    )}
                  </div>
                  <div className="footer-actions">
                    <button
                      className="btn-action btn-view"
                      onClick={() => handleView(mov)}
                      title="Visualizar"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDelete(mov)}
                      title="Excluir"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Paginação */}
      {pagination.totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="btn-pagination"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
          >
            Anterior
          </button>
          <span>
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
          </span>
          <button
            className="btn-pagination"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page >= pagination.totalPages}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  )
}

export default MovimentacoesPage
